import { civilFromParts, partsOf } from './civil.js'
import type { CivilDate } from './civil.js'
import { referenceMonthOf } from './reference-month.js'
import type { ReferenceMonth } from './reference-month.js'

/**
 * **O único lugar do repositório que converte instante em data civil — e o
 * único que pergunta que instante é agora.**
 *
 * A nota `Docs/03-Arquitetura/Dinheiro, datas e fuso.md` promete que existe um
 * lugar só para corrigir se as regras de fuso mudarem. Este arquivo é esse
 * lugar, e a promessa só é verdadeira porque nada mais em `src/` toca
 * `Intl.DateTimeFormat` nem `new Date()` — há regra de ESLint cobrando isso.
 *
 * As duas responsabilidades moram juntas de propósito. "Que dia é hoje?" é
 * `civilDateOf(agora)`, e separá-las em dois arquivos poria `new Date()` num
 * segundo lugar — a frase da nota deixaria de ser literal, que é justamente o
 * que ela tem de valioso.
 */

/** Fixo. Vive também em `Household.settings.timezone`, mas nunca varia. */
export const HOUSEHOLD_TIMEZONE = 'America/Sao_Paulo'

/**
 * `Intl` com `timeZone` explícito, **nunca** aritmética de offset.
 *
 * O Brasil não tem horário de verão desde 2019, então somar -3h acertaria hoje
 * — e erraria em todo instante anterior a 2019, e voltaria a errar no dia em
 * que o horário de verão voltar. O ICU carrega o banco de fusos; a subtração
 * carrega um palpite.
 *
 * `calendar` e `numberingSystem` explícitos para que o resultado não dependa de
 * nada do ambiente de quem roda: sem eles, um host configurado com outro
 * calendário ou com dígitos não-latinos devolveria partes que `Number()` não
 * entende.
 */
const FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: HOUSEHOLD_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  calendar: 'gregory',
  numberingSystem: 'latn',
})

/** Lê uma parte pelo tipo. `formatToParts` para não depender da ordem nem dos separadores do locale. */
function partOf(parts: Intl.DateTimeFormatPart[], type: 'year' | 'month' | 'day'): number {
  const found = parts.find((part) => part.type === type)
  if (!found) {
    throw new Error(`civilDateOf: o formatador não devolveu a parte "${type}"`)
  }
  return Number(found.value)
}

/**
 * Instante (`Timestamp` do Firestore, `Date` do SDK) → data civil na casa.
 *
 * Uma compra às 21h de 30/06 é de 30/06, e é isso que decide em que mês ela
 * aparece. Passar pelo fuso do navegador é o caminho mais curto para ela
 * aparecer em julho.
 */
export function civilDateOf(instant: Date): CivilDate {
  if (Number.isNaN(instant.getTime())) {
    throw new Error('civilDateOf: instante inválido')
  }
  const parts = FORMATTER.formatToParts(instant)
  return civilFromParts(partOf(parts, 'year'), partOf(parts, 'month'), partOf(parts, 'day'))
}

/**
 * Hoje, na casa. **A única leitura do relógio do ambiente em `src/`.**
 *
 * O `new Date()` sem argumento é exatamente o que o lint proíbe em todo o resto
 * do repositório (decisão 79), e o motivo de a exceção caber aqui é que ele não
 * escapa: o instante sai daqui já convertido pelo fuso da casa, nunca pelo do
 * navegador. Um `new Date().toISOString().slice(0, 10)` numa tela seria a
 * mesma linha com o fuso errado — às 21h de 30/06 em São Paulo ele responde
 * `'2026-07-01'`, e o mês default da tela de Transações pularia para julho
 * antes da hora.
 *
 * Quem precisa de "hoje" congelado — todo teste de domínio — não chama isto:
 * passa a data como parâmetro. Esta função existe para o único caso em que
 * ninguém tem como passar, que é a primeira renderização de uma tela.
 */
export function todayInHousehold(): CivilDate {
  return civilDateOf(new Date())
}

/** O mês de referência de hoje: `'2026-08'`. É o default do seletor de mês da
 * tela de Transações, e o único consumidor de `todayInHousehold` até aqui. */
export function currentReferenceMonth(): ReferenceMonth {
  return referenceMonthOf(todayInHousehold())
}

/**
 * O formatador que devolve o **relógio** da casa — data e hora — para um
 * instante. É o que permite responder "que horas são lá" sem somar offset.
 */
const RELOGIO = new Intl.DateTimeFormat('en-US', {
  timeZone: HOUSEHOLD_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
  calendar: 'gregory',
  numberingSystem: 'latn',
})

function numero(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): number {
  const achado = parts.find((part) => part.type === type)
  if (!achado) throw new Error(`instantInHousehold: falta a parte "${type}"`)
  // 24:00 é como o ICU escreve meia-noite com `hour12: false` em alguns
  // ambientes. Sem esta linha, meia-noite viraria o dia seguinte às 00:00 de um
  // cálculo e o mesmo dia às 24:00 de outro.
  const valor = Number(achado.value)
  return type === 'hour' && valor === 24 ? 0 : valor
}

/**
 * Data e hora **civis da casa** → instante ISO 8601.
 *
 * É o inverso de `civilDateOf`, e existe pelo lembrete da Fase 9: quem escreve
 * "amanhã às 18:00" quer 18:00 em São Paulo, não no fuso do celular — que pode
 * ser outro em viagem, e é sempre outro num teste rodando em UTC.
 *
 * **Não soma offset**: descobre quanto o relógio da casa marca no instante
 * chutado e corrige pela diferença. É o mesmo motivo do cabeçalho deste arquivo
 * — somar -3h acerta hoje e erra em todo instante anterior a 2019, e voltará a
 * errar no dia em que o horário de verão voltar.
 *
 * A correção é de uma passada. Ela erraria por uma hora se caísse **dentro** da
 * hora que uma transição de horário de verão pula, que é um instante que não
 * existe no relógio local; o Brasil não tem transição desde 2019, e o estrago
 * possível é um lembrete uma hora fora — não um dado financeiro errado.
 */
export function instantInHousehold(date: CivilDate, time: string): string {
  const [horaTexto, minutoTexto] = time.split(':')
  const hora = Number(horaTexto)
  const minuto = Number(minutoTexto)
  if (!Number.isInteger(hora) || !Number.isInteger(minuto)) {
    throw new Error(`instantInHousehold: hora inválida "${time}"`)
  }
  const { year, month, day } = partsOf(date)

  const chute = Date.UTC(year, month - 1, day, hora, minuto)
  const partes = RELOGIO.formatToParts(new Date(chute))
  const naCasa = Date.UTC(
    numero(partes, 'year'),
    numero(partes, 'month') - 1,
    numero(partes, 'day'),
    numero(partes, 'hour'),
    numero(partes, 'minute'),
    numero(partes, 'second'),
  )
  return new Date(chute - (naCasa - chute)).toISOString()
}

/** A hora civil da casa para um instante: `'18:30'`. Para a tela mostrar. */
export function timeInHousehold(instant: Date): string {
  const partes = RELOGIO.formatToParts(instant)
  const hh = String(numero(partes, 'hour')).padStart(2, '0')
  const mm = String(numero(partes, 'minute')).padStart(2, '0')
  return `${hh}:${mm}`
}
